import { Link } from '@tanstack/react-router';
import {
  Sparkles,
  Globe,
  Users,
  Image as ImageIcon,
  FileText,
  Shield,
  ArrowRight,
  Check,
  Wallet,
  Layers,
  Gauge,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicShell } from '@/components/layout/PublicShell';
import { usePlatformInfo } from '@/hooks/usePlatformInfo';
import { formatUsd } from '@/lib/utils';

export function LandingPage() {
  const { data } = usePlatformInfo();
  // Don't hardcode a trial fallback — the admin sets this from Platform →
  // Packages (trial row). Render a dash while loading so we don't flash a
  // stale marketing number that contradicts what they configured.
  const trialUsd = data?.trial_micro_usd;
  const trialDisplay = trialUsd != null ? formatUsd(trialUsd, 2) : '—';
  const textModels = data?.text_model_count ?? 100;
  const imageModels = data?.image_model_count ?? 50;
  const templates = data?.prompt_template_count ?? 40;
  const families = data?.text_model_families ?? [];

  return (
    <PublicShell>
      {/* Hero */}
      <section className="container pt-20 pb-14 text-center space-y-6">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">
          <Sparkles className="w-3 h-3" />
          Kayıt olunca <strong className="font-mono">{trialDisplay}</strong> hediye bakiye · kart istenmez
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight max-w-4xl mx-auto">
          AI ile Türkçe içerik üret,{' '}
          <span className="text-primary">WordPress'e saniyeler içinde</span> gönder
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          {textModels}+ yapay zeka dil modeli, {imageModels} görsel üretim modeli. Takımınla beraber
          birden fazla WordPress sitesi yönet, tek panelden tümüne yayın yap. Ödediğin her kuruşu
          gerçek kullanımla harca — süre kısıtı yok.
        </p>
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button size="lg" asChild>
            <Link to="/register">
              Ücretsiz Başla <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/pricing">Paketleri Gör</Link>
          </Button>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground pt-3">
          <span className="inline-flex items-center gap-1">
            <Check className="w-3 h-3 text-primary" /> Kart kaydı yok
          </span>
          <span className="inline-flex items-center gap-1">
            <Check className="w-3 h-3 text-primary" /> Süre kısıtı yok
          </span>
          <span className="inline-flex items-center gap-1">
            <Check className="w-3 h-3 text-primary" /> İstediğin zaman iptal
          </span>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y bg-muted/30">
        <div className="container grid grid-cols-2 md:grid-cols-4 gap-6 py-8">
          <Stat number={`${textModels}+`} label="Metin Modeli" hint="OpenAI, Claude, Gemini, Llama…" />
          <Stat number={`${imageModels}+`} label="Görsel Modeli" hint="DALL-E, Flux, Imagen, SD" />
          <Stat number={`${templates}+`} label="İçerik Şablonu" hint="Blog, tarif, inceleme, rehber…" />
          <Stat number="∞" label="Site & Takım" hint="Sınırsız site, editör daveti" />
        </div>
      </section>

      {/* Feature grid */}
      <section className="container py-16 space-y-10">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold">Neler var?</h2>
          <p className="text-muted-foreground">Tek panelden AI içerik üret + birden fazla WordPress sitesine otomatik yayınla.</p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Feature
            icon={Sparkles}
            title={`${textModels}+ AI dil modeli`}
            desc={`${families
              .map((f) => f.label.replace(/ .*$/, ''))
              .slice(0, 6)
              .join(', ')}… Her konuya uygun model seçebilirsin — GPT-5 nano'dan ($0.000001/kelime) Claude Opus 4.7'ye kadar.`}
          />
          <Feature
            icon={ImageIcon}
            title={`${imageModels}+ görsel modeli`}
            desc="Gemini 2.5 Flash Image (Nano Banana), Flux, DALL-E 3, Google Imagen 4, Seedream, Stable Diffusion. Featured + yazı içi görseller; sol/sağ/tam hizalama. WordPress media'ya otomatik yüklenir."
          />
          <Feature
            icon={FileText}
            title={`${templates}+ hazır içerik şablonu`}
            desc="SEO Blog, Nasıl Yapılır, Liste, Tarif, Ürün İncelemesi, Karşılaştırma, Alışveriş Rehberi, Seyahat, Sağlık, Finans Analizi, Maç Analizi, Burç Yorumu, URL yeniden yazım… Platform admin özel şablon da ekleyebilir."
          />
          <Feature
            icon={Globe}
            title="Çoklu site yönetimi"
            desc="WordPress Application Password ile tek tıkla bağlan. Organizasyonunda istediğin kadar site ekle, aynı içeriği farklı sitelere de yayınlayabilirsin."
          />
          <Feature
            icon={Users}
            title="Takım + editör davet"
            desc="Editör davet et, hangi sitelere erişebileceğini sen belirle. Her editör org bakiyesinden harcar, sen üyeleri çıkarana kadar çalışır."
          />
          <Feature
            icon={Wallet}
            title="USD bakiye, net fiyat"
            desc={`${trialDisplay} hediye bakiyenle ${data?.trial_sample.articles_at_cheapest ?? 5000}+ blog yazısı üretebilirsin (ekonomik model ile). Kredi kafa karışıklığı yok — ne harcadığını $ cinsinden 6 ondalıklı görürsün.`}
          />
          <Feature
            icon={Layers}
            title="Cron tabanlı otomatik üretim"
            desc={`Keyword havuzu + program. "Her gün 09:00'da 2 yazı üret, featured image ile yayına al" gibi kurallar. Hiçbir manuel tıklama gerekmez.`}
          />
          <Feature
            icon={Gauge}
            title="Bulk içerik üretimi"
            desc="Excel, keyword listesi, rakip URL, sitenin kendi archive'ı — 5 farklı input modundan toplu içerik. Tek seferde 100+ yazı kuyrukla."
          />
          <Feature
            icon={Shield}
            title="Şeffaf harcama kaydı"
            desc="Her üretimin maliyeti defterlenir — hangi modelle ne kadar yazı, kaç görsel, ne ödediğin tek tek görünür. Sürpriz kesinti veya gizli ek ücret yok."
          />
        </div>
      </section>

      {/* CTA band */}
      <section className="border-y bg-primary/5">
        <div className="container py-14 text-center space-y-4">
          <h2 className="text-3xl font-bold">
            {trialDisplay} hediye bakiyenle hemen dene
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Kart kaydı yok. {data?.trial_sample.articles_at_cheapest ?? '—'}+ blog yazısına yetecek bakiye.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button size="lg" asChild>
              <Link to="/register">
                Ücretsiz Hesap Oluştur <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/pricing">Paketler →</Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function Stat({ number, label, hint }: { number: string; label: string; hint: string }) {
  return (
    <div className="text-center">
      <div className="text-3xl md:text-4xl font-bold font-mono text-primary">{number}</div>
      <div className="text-sm font-medium mt-1">{label}</div>
      <div className="text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

function Feature({
  icon: Icon,
  title,
  desc,
}: {
  icon: typeof Sparkles;
  title: string;
  desc: string;
}) {
  return (
    <Card className="border-muted-foreground/10">
      <CardContent className="p-5 space-y-2.5">
        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
      </CardContent>
    </Card>
  );
}
